import { create } from 'zustand';
import apiClient from '../services/api/client';
import {
  login as apiLogin,
  logout as apiLogout,
  getToken,
  parseJWT,
  setToken,
} from '../services/api/auth';
import {
  acquireEntraAccessToken,
  clearMsalLocalSession,
  clearStoredAuthProvider,
  ensureMsalReady,
  getStoredAuthProvider,
  handleLoginRedirectReturn,
  hasMsalSpaRegistration,
  isMsalConfigured,
  loginRedirectToMicrosoft,
  logoutMicrosoftRedirect,
  setStoredAuthProvider,
} from '../auth/msalService';
import { formatEntraLoginFailure, saveLoginError } from '../utils/loginErrorMessage';
import { saveReturnUrlAfterLogin } from '../utils/returnUrl';

/**
 * Authentication store using Zustand
 */
const useAuthStore = create((set, get) => ({
  user: null,
  token: getToken(),
  role: null,
  isAuthenticated: false,
  authProvider: /** @type {'legacy' | 'entra' | null} */ (getStoredAuthProvider()),
  _expiryTimerId: null,

  _clearExpiryTimer: () => {
    const timerId = get()._expiryTimerId;
    if (timerId) {
      clearTimeout(timerId);
      set({ _expiryTimerId: null });
    }
  },

  logoutAndRedirectToLogin: async (reason) => {
    get()._clearExpiryTimer();
    const provider = get().authProvider;
    if (provider === 'entra') {
      clearStoredAuthProvider();
      await clearMsalLocalSession();
      set({
        user: null,
        token: null,
        role: null,
        isAuthenticated: false,
        authProvider: null,
      });
      saveReturnUrlAfterLogin();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return;
    }
    apiLogout();
    clearStoredAuthProvider();
    set({
      user: null,
      token: null,
      role: null,
      isAuthenticated: false,
      authProvider: null,
    });
    saveReturnUrlAfterLogin();
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  },

  _scheduleExpiryCheck: (token) => {
    get()._clearExpiryTimer();
    if (get().authProvider === 'entra') {
      return;
    }
    const decoded = parseJWT(token);
    if (!decoded?.exp) return;

    const expMs = decoded.exp * 1000;
    const nowMs = Date.now();

    if (expMs <= nowMs) {
      get().logoutAndRedirectToLogin('token_expired');
      return;
    }

    const msUntilExpiry = expMs - nowMs;
    const timerId = setTimeout(() => {
      set({ _expiryTimerId: null });
      get().logoutAndRedirectToLogin('token_expired');
    }, msUntilExpiry);
    set({ _expiryTimerId: timerId });
  },

  _syncProfileFromApi: async () => {
    const res = await apiClient.get('/me');
    const { username, role } = res.data;
    set({
      user: { username, role },
      role,
      isAuthenticated: true,
      authProvider: 'entra',
    });
  },

  /**
   * Call once at startup (after MSAL handleRedirectPromise).
   */
  bootstrapAuth: async () => {
    if (hasMsalSpaRegistration()) {
      await ensureMsalReady();
      let redirectResult = null;
      try {
        redirectResult = await handleLoginRedirectReturn();
      } catch (error) {
        saveLoginError(formatEntraLoginFailure(error));
        return;
      }
      if (redirectResult?.account) {
        setStoredAuthProvider('entra');
        try {
          await get()._syncProfileFromApi();
        } catch (error) {
          set({
            isAuthenticated: false,
            user: null,
            role: null,
            authProvider: null,
          });
          clearStoredAuthProvider();
          saveLoginError(formatEntraLoginFailure(error));
        }
        return;
      }
    }

    const token = getToken();
    const stored = getStoredAuthProvider();
    const legacyOk = token && (stored === 'legacy' || stored === null);

    if (legacyOk) {
      const decoded = parseJWT(token);
      setStoredAuthProvider('legacy');
      set({
        token,
        role: decoded?.role || localStorage.getItem('role'),
        isAuthenticated: true,
        user: {
          username: localStorage.getItem('username') || decoded?.sub,
          role: decoded?.role || localStorage.getItem('role'),
        },
        authProvider: 'legacy',
      });
      get()._scheduleExpiryCheck(token);
      return;
    }

    if (isMsalConfigured()) {
      const msal = await ensureMsalReady();
      if (msal?.getAllAccounts().length) {
        const t = await acquireEntraAccessToken();
        if (t) {
          setStoredAuthProvider('entra');
          try {
            await get()._syncProfileFromApi();
          } catch (error) {
            clearStoredAuthProvider();
            if (
              typeof window !== 'undefined' &&
              window.location.pathname === '/login'
            ) {
              saveLoginError(formatEntraLoginFailure(error));
            }
          }
        }
      }
    }
  },

  login: async (username, password) => {
    try {
      const data = await apiLogin(username, password);
      const decoded = parseJWT(data.access_token);

      setStoredAuthProvider('legacy');
      setToken(data.access_token, {
        role: decoded?.role,
        username,
      });

      set({
        token: data.access_token,
        role: decoded?.role,
        isAuthenticated: true,
        authProvider: 'legacy',
        user: {
          username,
          role: decoded?.role,
        },
      });

      get()._scheduleExpiryCheck(data.access_token);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      let message = error.message || 'Inloggen mislukt';
      let attemptsRemaining = null;
      let retryAfterMinutes = null;

      if (detail) {
        if (typeof detail === 'object') {
          message = detail.message || message;
          attemptsRemaining = detail.attempts_remaining;
          retryAfterMinutes = detail.retry_after_minutes;
        } else {
          message = String(detail);
        }
      }

      return {
        success: false,
        error: message,
        attemptsRemaining: attemptsRemaining ?? undefined,
        retryAfterMinutes: retryAfterMinutes ?? undefined,
        status,
      };
    }
  },

  loginWithMicrosoft: async () => {
    if (!isMsalConfigured()) {
      return {
        success: false,
        error:
          'Microsoft-login: zet VITE_AZURE_AD_TENANT_ID, VITE_AZURE_AD_SPA_CLIENT_ID en VITE_AZURE_AD_API_SCOPE in .env.local (zie .env.example), daarna npm run dev opnieuw starten.',
      };
    }
    try {
      await loginRedirectToMicrosoft();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatEntraLoginFailure(error),
      };
    }
  },

  logout: () => {
    get()._clearExpiryTimer();
    const provider = get().authProvider;
    if (provider === 'entra') {
      clearStoredAuthProvider();
      logoutMicrosoftRedirect();
    } else {
      apiLogout();
      clearStoredAuthProvider();
    }
    set({
      user: null,
      token: null,
      role: null,
      isAuthenticated: false,
      authProvider: null,
    });
  },
}));

if (typeof window !== 'undefined') {
  window.addEventListener('sessionExpired', () => {
    void useAuthStore.getState().logoutAndRedirectToLogin('session_expired');
  });
}

export default useAuthStore;
